import { addMonths, format, isValid, parse, parseISO } from "date-fns";

// The care plan's next review date can come back in either shape: the
// CarePlanDto declares it as a LocalDate (ISO, "2028-12-30"), but when the plan
// round-trips through the review's clinical_data blob it arrives as a compact
// HL7 date ("20281230"), which then leaked to the UI verbatim. Parse both.
const HL7_COMPACT_DATE = /^\d{8}$/;

const parseCarePlanDate = (value) => {
  if (!value) return null;

  // Jackson serialises a LocalDate as a [year, month, day] array (month is
  // 1-based), which React then renders separator-less ("2027116" for
  // [2027, 1, 16]). Rebuild the date from the parts before the string paths.
  if (Array.isArray(value)) {
    const [year, month, day] = value;

    if ([year, month, day].some((part) => typeof part !== "number")) return null;

    const parsed = new Date(year, month - 1, day);

    return isValid(parsed) ? parsed : null;
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  const parsed = HL7_COMPACT_DATE.test(trimmed)
    ? parse(trimmed, "yyyyMMdd", new Date())
    : parseISO(trimmed);

  return isValid(parsed) ? parsed : null;
};

// For display. Falls back to the raw value rather than hiding an unparseable
// date — better a visible oddity than a silently blank clinical field.
export const formatCarePlanDate = (value) => {
  const date = parseCarePlanDate(value);

  return date ? format(date, "dd-MM-yyyy") : value ?? "";
};

// For seeding the date picker, which expects an ISO date.
export const toCarePlanIsoDate = (value) => {
  const date = parseCarePlanDate(value);

  return date ? format(date, "yyyy-MM-dd") : "";
};

// Editable default for "Next review date" when a plan has none saved yet, so the
// clinician isn't left working the interval out by hand (KIN-1360). Uses the
// recommended review interval per programme — CDM = 6 months, PP = 12 months —
// and the clinician can still change it (e.g. bring a patient back sooner). OCF
// has no care-plan step, so its 5-year interval isn't handled here.
const CDM_NEXT_REVIEW_MONTHS = 6;
const PP_NEXT_REVIEW_MONTHS = 12;

export const defaultNextReviewDate = (isPp) =>
  format(
    addMonths(new Date(), isPp ? PP_NEXT_REVIEW_MONTHS : CDM_NEXT_REVIEW_MONTHS),
    "yyyy-MM-dd"
  );
